import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Share2, Phone, Mail, UserPlus, Stethoscope,
  MessageCircle, User, Heart, Link2, Loader2, Copy, Check,
} from "lucide-react";
import { api, fmtDate } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import DoctorsManager from "./DoctorsManager";

// ---------------------------------------------------------------------------
// Phone-number normaliser. wa.me wants a digits-only number with the country
// code. Indian numbers entered locally (10 digits, or "0" + 10) get +91
// auto-prepended; everything else with a leading "+" is left as-is.
// ---------------------------------------------------------------------------
function normalisePhone(raw) {
  const trimmed = (raw || "").trim();
  let digits = trimmed.replace(/\D/g, "");
  if (digits && !trimmed.startsWith("+")) {
    if (digits.length === 10) digits = "91" + digits;
    else if (digits.length === 11 && digits.startsWith("0")) digits = "91" + digits.slice(1);
  }
  return digits;
}

function buildShareLinks(phone, email, subject, body) {
  const digits = normalisePhone(phone);
  const enc = (s) => encodeURIComponent(s);
  return {
    whatsapp: digits ? `https://wa.me/${digits}?text=${enc(body)}` : null,
    mailto: email ? `mailto:${email}?subject=${enc(subject)}&body=${enc(body)}` : null,
  };
}

/**
 * Share modal — generates a signed 7-day share link and opens WhatsApp /
 * Email with the message + link pre-filled. Three recipient tabs:
 *   • Doctor    — picks from the global doctors phonebook
 *   • Athlete   — uses the sailor's own mobile + email from baseline
 *   • Guardian  — uses the sailor's guardian fields from baseline
 *
 * Props:
 *   open, onClose
 *   sailorId      : string  (required to mint a link)
 *   sailorName    : string  (header)
 *   baseline      : object  (sailor.baseline — for athlete/guardian contacts)
 *   reportType    : "full" | "issues" | "latest"
 *   subject       : string  (email subject line)
 *   bodyTemplate  : string  (use {dr} for the recipient name; {url} is auto-appended on a new line)
 *   contextLabel  : string  (sub-heading)
 */
const TABS = [
  { id: "doctor",   label: "Doctor",   icon: Stethoscope },
  { id: "athlete",  label: "Athlete",  icon: User },
  { id: "guardian", label: "Guardian", icon: Heart },
];

export default function ShareModal({
  open, onClose,
  sailorId, sailorName, baseline,
  reportType = "full", subject, bodyTemplate,
  contextLabel,
}) {
  // Squad-wide reports don't have a single sailor → only the doctor tab is
  // meaningful and we skip the signed-link feature entirely (the coach
  // attaches the PDF manually). We detect this via the absence of sailorId.
  const isSquadMode = !sailorId;
  const [tab, setTab] = useState("doctor");
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // The signed link is minted lazily on first share so we don't burn a token
  // every time the modal opens. Once minted we keep it for the modal's life.
  const [link, setLink] = useState(null); // { token, url, expires_at }
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [copied, setCopied] = useState(false);

  useEscClose(open && !showManager, onClose);

  const loadDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/doctors");
      setDoctors(r.data);
      if (r.data.length > 0) setSelectedDoctorId(r.data[0].id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state every open
  useEffect(() => {
    if (!open) return;
    setTab("doctor");
    setError("");
    setLinkError("");
    setLink(null);
    setCopied(false);
    setSelectedDoctorId(null);
    loadDoctors();
  }, [open, loadDoctors]);

  // ----------------- derive recipient from active tab ---------------------
  const recipient = useMemo(() => {
    if (tab === "doctor") {
      const d = doctors.find((x) => x.id === selectedDoctorId);
      if (!d) return null;
      return {
        type: "doctor", name: d.name, phone: d.phone, email: d.email,
        meta: d.specialty || "",
      };
    }
    if (tab === "athlete") {
      const phone = baseline?.mobile || "";
      const email = baseline?.athlete_email || "";
      if (!phone && !email) return { type: "athlete", name: sailorName, phone: "", email: "", missing: true };
      return { type: "athlete", name: sailorName, phone, email, meta: "Athlete" };
    }
    if (tab === "guardian") {
      const name = baseline?.parent_guardian || "Guardian";
      const phone = baseline?.guardian_mobile || "";
      const email = baseline?.guardian_email || "";
      if (!phone && !email) return { type: "guardian", name, phone: "", email: "", missing: true };
      return {
        type: "guardian", name, phone, email,
        meta: baseline?.relationship ? `${baseline.relationship} of ${sailorName}` : `Guardian of ${sailorName}`,
      };
    }
    return null;
  }, [tab, doctors, selectedDoctorId, baseline, sailorName]);

  // Personalised message body — replace {dr} with recipient name and append
  // the signed URL on its own line for tap-friendliness in WhatsApp.
  const personalisedBody = useMemo(() => {
    const baseBody = (bodyTemplate || "").replace(/\{dr\}/g, recipient?.name || "");
    if (!link?.url) return baseBody;
    // If {url} placeholder exists, substitute. Otherwise append.
    if (/\{url\}/.test(baseBody)) return baseBody.replace(/\{url\}/g, link.url);
    return `${baseBody}\n\n${link.url}`;
  }, [bodyTemplate, recipient, link]);

  const { whatsapp, mailto } = useMemo(
    () => recipient ? buildShareLinks(recipient.phone, recipient.email, subject, personalisedBody) : { whatsapp: null, mailto: null },
    [recipient, subject, personalisedBody],
  );

  // Mint the share token (once) — called right before the user actually
  // shares so revoked/expired links don't accumulate from idle opens.
  const ensureLink = async () => {
    if (link) return link;
    // Squad-wide reports can't be packaged into a single sailor's share link —
    // skip minting and let the share fall back to message-text-only (the
    // coach attaches the PDF manually).
    if (isSquadMode) return null;
    setLinkLoading(true);
    setLinkError("");
    try {
      const r = await api.post("/share/create", {
        sailor_id: sailorId,
        report_type: reportType,
        recipient_type: recipient?.type || "other",
        recipient_name: recipient?.name || null,
      });
      setLink(r.data);
      return r.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't generate a share link.";
      setLinkError(typeof msg === "string" ? msg : JSON.stringify(msg));
      return null;
    } finally {
      setLinkLoading(false);
    }
  };

  const fireShare = async (kind) => {
    if (!recipient || recipient.missing) return;
    const minted = await ensureLink();   // returns null in squad mode → OK
    // Bump doctor's last_used_at so phonebook reorders. Fire-and-forget.
    if (tab === "doctor" && selectedDoctorId) {
      api.post(`/doctors/${selectedDoctorId}/touch`).catch(() => {});
    }
    const bodyWithName = (bodyTemplate || "").replace(/\{dr\}/g, recipient.name || "");
    const finalBody = minted
      ? (/\{url\}/.test(bodyWithName)
          ? bodyWithName.replace(/\{url\}/g, minted.url)
          : `${bodyWithName}\n\n${minted.url}`)
      : bodyWithName;
    const links = buildShareLinks(recipient.phone, recipient.email, subject, finalBody);
    const url = kind === "whatsapp" ? links.whatsapp : links.mailto;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    const minted = await ensureLink();
    if (!minted) return;
    await navigator.clipboard?.writeText(minted.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[180] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4"
        data-testid="share-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div className="card w-full max-w-xl p-5 md:p-6">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                <Share2 size={12} /> Share medical record
              </div>
              <h2 className="font-display text-xl text-slate-100 mt-1">
                {contextLabel || "Share via WhatsApp / Email"}
              </h2>
              <div className="text-[11px] text-slate-400 mt-1">
                Picks the right contact and pre-fills the message with a secure 7-day link.
                The recipient just taps the link to open the report on any phone.
              </div>
            </div>
            <button onClick={onClose} className="pill !p-1.5" data-testid="share-close-btn">
              <X size={14} />
            </button>
          </div>

          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
              {error}
            </div>
          )}

          {/* Recipient tabs — hidden in squad mode since athlete/guardian aren't meaningful for squad-wide reports */}
          {!isSquadMode && (
            <div className="flex items-center gap-1 mb-3 border-b border-[#1E304B]">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-3 py-2 text-xs flex items-center gap-1.5 border-b-2 -mb-px transition ${
                      active
                        ? "border-cyan-400 text-cyan-200"
                        : "border-transparent text-slate-400 hover:text-slate-200"
                    }`}
                    data-testid={`share-tab-${t.id}`}
                  >
                    <Icon size={12} /> {t.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Recipient body */}
          {tab === "doctor" ? (
            <DoctorPicker
              doctors={doctors} loading={loading}
              selectedId={selectedDoctorId} onSelect={setSelectedDoctorId}
              onManage={() => setShowManager(true)}
            />
          ) : (
            <ContactCard recipient={recipient} sailorName={sailorName} tab={tab} />
          )}

          {/* Link & actions */}
          {recipient && !recipient.missing && (
            <div className="mt-4">
              {!isSquadMode && (
                <LinkRow
                  link={link}
                  loading={linkLoading}
                  error={linkError}
                  onCopy={copyLink}
                  copied={copied}
                />
              )}
              {isSquadMode && (
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-2 text-amber-200 text-[11px]">
                  Squad-wide reports can&apos;t generate a single share link.
                  Download the PDF first, then attach it manually after WhatsApp / Email opens.
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => fireShare("whatsapp")}
                  disabled={!recipient.phone || linkLoading}
                  className="pill active flex-1 justify-center disabled:opacity-40"
                  data-testid="share-whatsapp-btn"
                  title={recipient.phone ? "Open WhatsApp with message + link pre-filled" : "No phone number on file"}
                >
                  {linkLoading ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                  WhatsApp
                </button>
                <button
                  onClick={() => fireShare("mailto")}
                  disabled={!recipient.email || linkLoading}
                  className="pill flex-1 justify-center disabled:opacity-40"
                  data-testid="share-email-btn"
                  title={recipient.email ? "Open your email client with message + link" : "No email on file"}
                >
                  {linkLoading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Email
                </button>
              </div>
              {(!recipient.phone || !recipient.email) && (
                <div className="mt-2 text-[10px] text-slate-500">
                  Missing {[!recipient.phone && "phone", !recipient.email && "email"].filter(Boolean).join(" / ")}{" "}
                  for this {recipient.type}.{" "}
                  {tab === "doctor" ? (
                    <button onClick={() => setShowManager(true)} className="text-cyan-300 underline">Edit in phonebook</button>
                  ) : (
                    <span>Edit in the baseline assessment.</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DoctorsManager
        open={showManager}
        onClose={() => setShowManager(false)}
        onChanged={loadDoctors}
      />
    </>,
    document.body,
  );
}

// ---------------- sub-components ----------------

function DoctorPicker({ doctors, loading, selectedId, onSelect, onManage }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-400">Pick from phonebook</div>
        <button onClick={onManage} className="pill !text-[11px] !py-1 !px-2" data-testid="share-manage-doctors">
          <UserPlus size={11} /> Manage
        </button>
      </div>
      {loading ? (
        <div className="py-4 text-center text-slate-500 text-sm">Loading…</div>
      ) : doctors.length === 0 ? (
        <div className="border border-dashed border-slate-700 rounded-lg p-4 text-center" data-testid="share-empty-state">
          <div className="text-slate-300 text-sm">No doctors in your phonebook yet.</div>
          <button onClick={onManage} className="pill active mt-2" data-testid="share-add-first-doctor">
            <UserPlus size={13} /> Add your first doctor
          </button>
        </div>
      ) : (
        <div className="max-h-[220px] overflow-y-auto border border-[#1E304B] rounded-lg divide-y divide-[#1E304B]">
          {doctors.map((d) => {
            const active = d.id === selectedId;
            return (
              <button
                key={d.id}
                onClick={() => onSelect(d.id)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition ${active ? "bg-cyan-500/10" : "hover:bg-[#0E1A2E]"}`}
                data-testid={`share-doctor-${d.id}`}
              >
                <Stethoscope size={14} className={active ? "text-cyan-300" : "text-slate-500"} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-100 truncate">{d.name}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                    {d.specialty && <span>{d.specialty}</span>}
                    {d.phone && <span className="font-mono-data inline-flex items-center gap-1"><Phone size={9} /> {d.phone}</span>}
                    {d.email && <span className="font-mono-data inline-flex items-center gap-1 truncate"><Mail size={9} /> {d.email}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContactCard({ recipient, sailorName, tab }) {
  const label = tab === "athlete" ? "Athlete contact" : "Guardian contact";
  if (!recipient || recipient.missing) {
    return (
      <div className="border border-dashed border-amber-500/40 bg-amber-500/5 rounded-lg p-4" data-testid={`share-${tab}-missing`}>
        <div className="text-amber-200 text-sm font-medium">
          {sailorName} has no {tab === "athlete" ? "athlete" : "guardian"} contact details on file.
        </div>
        <div className="text-[11px] text-amber-200/70 mt-1">
          Add them in the athlete&apos;s Baseline Assessment → Identification section
          ({tab === "athlete"
            ? "Athlete Mobile / Athlete Email"
            : "Parent / Guardian, Guardian Mobile, Guardian Email"}).
        </div>
      </div>
    );
  }
  return (
    <div className="border border-[#1E304B] rounded-lg p-3" data-testid={`share-${tab}-card`}>
      <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      <div className="text-sm text-slate-100">{recipient.name}</div>
      {recipient.meta && <div className="text-[11px] text-slate-500 mt-0.5">{recipient.meta}</div>}
      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-300 font-mono-data">
        {recipient.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {recipient.phone}</span>}
        {recipient.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {recipient.email}</span>}
      </div>
    </div>
  );
}

function LinkRow({ link, loading, error, onCopy, copied }) {
  if (error) {
    return (
      <div className="border border-rose-500/40 bg-rose-500/5 rounded-md px-3 py-2 text-rose-200 text-xs">
        {error}
      </div>
    );
  }
  return (
    <div className="border border-[#1E304B] bg-[#0a1422] rounded-md px-3 py-2 flex items-center gap-2" data-testid="share-link-row">
      <Link2 size={12} className="text-cyan-300 shrink-0" />
      <div className="text-[11px] text-slate-300 truncate flex-1 font-mono-data">
        {loading ? "Generating secure link…"
          : link ? link.url
          : "Link will be generated when you click WhatsApp or Email."}
      </div>
      {link && (
        <>
          <span className="text-[10px] text-slate-500 hidden sm:inline whitespace-nowrap">
            expires {fmtDate(link.expires_at?.slice(0, 10))}
          </span>
          <button
            onClick={onCopy}
            className="pill !text-[11px] !py-1 !px-2 shrink-0"
            data-testid="share-copy-link"
            title="Copy the link"
          >
            {copied ? <Check size={11} className="text-emerald-300" /> : <Copy size={11} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </>
      )}
    </div>
  );
}
